import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CheckSquare, Mail } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Failed to send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center auth-bg grain-bg p-6" style={{ paddingTop: "max(1.5rem, env(safe-area-inset-top))", paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
            <CheckSquare size={20} className="text-primary-foreground" />
          </div>
          <span className="font-display font-extrabold text-lg">FlowTasks<span className="text-primary">360</span></span>
        </div>
        <h2 className="font-display text-3xl font-bold mb-2">Forgot password?</h2>
        <p className="text-sm text-muted-foreground mb-7">Enter your email and we'll send you a secure reset link. Link expires in 1 hour.</p>

        {sent ? (
          <div data-testid="forgot-sent" className="rounded-lg border border-primary/30 bg-primary/5 p-5 text-sm">
            <div className="font-display font-bold mb-1">Check your inbox</div>
            <p className="text-muted-foreground">
              If an account exists for <span className="text-foreground">{email}</span>, a reset email is on its way.
            </p>
            <Link to="/login" className="text-primary text-sm mt-4 inline-block hover:underline">← Back to sign in</Link>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
              <div className="relative mt-1.5">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="email"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-9 h-11"
                  placeholder="you@example.com"
                />
              </div>
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{error}</div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-11" data-testid="forgot-submit-btn">
              {loading ? "Sending…" : "Send reset link"}
            </Button>
            <p className="text-sm text-muted-foreground text-center">
              <Link to="/login" className="hover:underline">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
